/**
 * 라우팅 — SPA. 서버 라우팅은 없다(nginx 가 모든 경로를 index.html 로 폴백).
 *
 * 화면은 F-2 이후 늘어난다. 지금 고정하는 것은 **경계**뿐이다: 인증이 필요한 영역과 그렇지
 * 않은 영역, 그리고 관리자 영역. 이 경계가 나중에 화면을 따라다니면 P2(세션 만료 시 전 화면
 * 로그인 유도)를 화면마다 다시 구현하게 된다.
 */
import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { FullscreenNotice, RequireAdmin, RequireAuth, SessionProvider } from '@/auth/SessionProvider'
import { UnauthorizedError } from '@/api/client'
import ChatPage from '@/routes/ChatPage'
import LoginPage from '@/routes/LoginPage'

// 채팅과 로그인만 즉시 로드한다 — 앱을 열면 반드시 그중 하나를 본다.
// 나머지는 **눌러야 보이는 화면**이라 초기 로드에 실을 이유가 없다. 특히 관리자 화면은 사내
// 운영자만 들어가는데 전 사용자가 그 코드를 내려받고 있었다.
const WorkListPage = lazy(() => import('@/routes/WorkListPage'))
const FileCabinetPage = lazy(() => import('@/routes/FileCabinetPage'))
const AdminPage = lazy(() => import('@/routes/AdminPage'))
const ProjectPage = lazy(() => import('@/routes/ProjectPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 401 은 재시도해도 401 이다 — 만료된 세션으로 백엔드를 두드리지 않는다.
      retry: (failureCount, error) => !(error instanceof UnauthorizedError) && failureCount < 2,
      staleTime: 10_000,
    },
  },
})

function NotFound() {
  const { t } = useTranslation()
  return <FullscreenNotice>{t('common.not_found')}</FullscreenNotice>
}

/** 지연 로드 대기 — 사내망이라 순간이지만, 빈 화면 대신 무엇을 기다리는지 말한다. */
function RouteLoading() {
  const { t } = useTranslation()
  return <FullscreenNotice>{t('common.loading')}</FullscreenNotice>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <ChatPage />
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <RequireAuth>
                  <ProjectPage />
                </RequireAuth>
              }
            />
            <Route
              path="/jobs"
              element={
                <RequireAuth>
                  <WorkListPage />
                </RequireAuth>
              }
            />
            <Route
              path="/files"
              element={
                <RequireAuth>
                  <FileCabinetPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminPage />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
